import { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import { supabase } from "@/integrations/supabase/client";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Upload, User } from "lucide-react";
import { toast } from "sonner";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ImageCropDialog } from "./ImageCropDialog";
import { AvatarViewer } from "./AvatarViewer";

interface AvatarUploadProps {
  userId: string;
  currentAvatarUrl?: string | null;
  username?: string | null;
  size?: "sm" | "md" | "lg";
  showUploadButton?: boolean;
  onUploadComplete?: () => void;
  clickable?: boolean; // If true, clicking the avatar triggers upload
  viewable?: boolean; // If true, clicking the avatar opens fullscreen viewer
  uploadInputId?: string; // Custom ID for the file input
}

const sizeClasses = {

  sm: "h-8 w-8",
  md: "h-10 w-10",
  lg: "h-20 w-20",
};

export const AvatarUpload = ({ 
  userId, 
  currentAvatarUrl, 
  username,
  size = "md",
  showUploadButton = false,
  onUploadComplete,
  clickable = false,
  viewable = false,
  uploadInputId = "avatar-upload"
}: AvatarUploadProps) => {
  console.log('[AvatarUpload] Rendering');
  const { t } = useTranslation();
  console.log('[AvatarUpload] useTranslation hook called successfully');
  const [uploading, setUploading] = useState(false);
  const [showCropDialog, setShowCropDialog] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [localPreviewUrl, setLocalPreviewUrl] = useState<string | null>(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [showViewer, setShowViewer] = useState(false);
  const queryClient = useQueryClient();

  // Preload the avatar image and track when it's loaded
  useEffect(() => {
    if (currentAvatarUrl && !localPreviewUrl) {
      setImageLoaded(false);
      const img = new Image();
      img.onload = () => setImageLoaded(true);
      img.onerror = () => setImageLoaded(false);
      img.src = currentAvatarUrl;
      
      // If image is already cached, onload fires synchronously
      if (img.complete) {
        setImageLoaded(true);
      }
    } else if (localPreviewUrl) {
      setImageLoaded(true);
    }
  }, [currentAvatarUrl, localPreviewUrl]);

  const uploadAvatar = useMutation({
    mutationFn: async (blob: Blob) => {
      const fileExt = 'jpg';
      const filePath = `${userId}/avatar.${fileExt}`;

      // Delete old avatar if exists
      if (currentAvatarUrl) {
        const oldPath = currentAvatarUrl.split('/').slice(-2).join('/');
        await supabase.storage.from('avatars').remove([oldPath]);
      }

      // Upload new avatar
      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, blob, { upsert: true, contentType: 'image/jpeg' });

      if (uploadError) throw uploadError;

      // Get public URL with cache-busting timestamp
      // This ensures the new avatar is fetched everywhere (leaderboard, profiles, etc.)
      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);
      
      // Add timestamp to bust browser cache across all components
      const cacheBustedUrl = `${publicUrl}?t=${Date.now()}`;

      // Update profile with cache-busted URL
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ avatar_url: cacheBustedUrl })
        .eq('id', userId);

      if (updateError) throw updateError;

      return cacheBustedUrl;
    },
    onSuccess: () => {
      // Invalidate profile queries for this user
      queryClient.invalidateQueries({ queryKey: ['profile', userId] });
      queryClient.invalidateQueries({ queryKey: ['profile-avatar', userId] });
      // Also invalidate leaderboard to show updated avatar
      queryClient.invalidateQueries({ queryKey: ['leaderboard'] });
      toast.success(t('notifications.toast.profile_picture_updated'));
      if (onUploadComplete) {
        onUploadComplete();
      }
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to upload image');
    }
  });

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error(t('notifications.toast.please_upload_an_image_file'));
      return;
    }

    // Validate file size (max 5MB before cropping)
    if (file.size > 5 * 1024 * 1024) {
      toast.error(t('notifications.toast.image_must_be_less_than_5mb'));
      return;
    }

    // Create preview URL for cropper
    const reader = new FileReader();
    reader.onload = () => {
      setSelectedImage(reader.result as string);
      setShowCropDialog(true);
    };
    reader.readAsDataURL(file);

    // Reset input so the same file can be selected again
    e.target.value = '';
  };

  const handleCropComplete = async (croppedImageBlob: Blob) => {
    setShowCropDialog(false);
    setSelectedImage(null);
    
    // Create local preview URL for immediate display
    const previewUrl = URL.createObjectURL(croppedImageBlob);
    setLocalPreviewUrl(previewUrl);
    setUploading(true);
    
    try {
      await uploadAvatar.mutateAsync(croppedImageBlob);
    } finally {
      setUploading(false);
      // Clean up the blob URL after upload
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
        setLocalPreviewUrl(null);
      }
    }
  };

  const getInitials = () => {
    if (!username) return 'U';
    return username.charAt(0).toUpperCase();
  };

  const handleAvatarClick = () => {
    if (clickable) {
      document.getElementById(uploadInputId)?.click();
    } else if (viewable && currentAvatarUrl) {
      setShowViewer(true);
    }
  };

  const isClickable = clickable || (viewable && currentAvatarUrl);

  return (
    <>
      <div className="flex items-center gap-3">
        <div 
          className={isClickable ? "cursor-pointer" : ""}
          onClick={isClickable ? handleAvatarClick : undefined}
        >
          <Avatar className={sizeClasses[size]}>
            {(localPreviewUrl || currentAvatarUrl) && imageLoaded ? (
              <AvatarImage
                src={localPreviewUrl || currentAvatarUrl}
                alt={username || 'User'}
                loading="eager"
                className="object-cover"
              />
            ) : (
              <AvatarFallback className="bg-muted">
                <User className="w-1/2 h-1/2 text-muted-foreground" />
              </AvatarFallback>
            )}
          </Avatar>
        </div>
        
        {showUploadButton && (
          <div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => document.getElementById(uploadInputId)?.click()}
              disabled={uploading}
            >
              <Upload className="w-4 h-4 mr-2" />
              {uploading ? 'Uploading...' : 'Upload'}
            </Button>
          </div>
        )}
      </div>

      <input
        type="file"
        id={uploadInputId}
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
        disabled={uploading}
      />

      {selectedImage && (
        <ImageCropDialog
          open={showCropDialog}
          onOpenChange={setShowCropDialog}
          imageSrc={selectedImage}
          onCropComplete={handleCropComplete}
          aspectRatio={1}
        />
      )}

      {viewable && currentAvatarUrl && (
        <AvatarViewer
          open={showViewer}
          onClose={() => setShowViewer(false)}
          avatarUrl={currentAvatarUrl}
          username={username}
        />
      )}
    </>
  );
};
